import * as TWEEN from '@tweenjs/tween.js'
import { CameraController, IThreeBurApp, MiniMapProps } from './ThreeCameraController'
import { ThreeLocation, UTMZONE } from './ThreeLocation'
import { MarkerController } from './ThreeMarkerController'
import { ThreeMaterials } from './ThreeMaterials'
import * as THREE from 'three'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js'
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass'
import { fromLatLon } from 'utm'
import { Asset, Location } from './openapi'

export const SPRITE_LAYER = 1
const ARCHAEOLOGY_LAYER = 2
const ARCHAEOLOGY_LAYER_MASK = 1 << ARCHAEOLOGY_LAYER

class ThreeBur3DView implements IThreeBurApp {
  private render_ = false
  private scene_: THREE.Scene
  private renderer_: THREE.WebGLRenderer
  private fboBg_: THREE.WebGLRenderTarget
  private fboFg_: THREE.WebGLRenderTarget
  private heightmapFbo_: THREE.WebGLRenderTarget
  private depthMaterial_: THREE.MeshDepthMaterial
  private heightmapData_: Uint8Array
  private loader_: GLTFLoader
  private clickedPosition_: [number, number]
  private raycaster_: THREE.Raycaster
  private pointerPosition_: THREE.Vector2
  private POIWasClickedCallback_: (s: number) => void
  private updateMinimapCallback: (p: number[], d: number[]) => void
  private updatePoiModeCallback: (enabled: boolean) => void
  private canvasContainer?: HTMLDivElement
  private opacityTransition_: { opacity: number }
  private pmremGenerator_: THREE.PMREMGenerator
  private shadowsEnabled_ = true
  private light_: THREE.Light[]
  private modelBoundingBox_: THREE.Box3
  private modelCenter_: THREE.Vector3
  private modelSize_: THREE.Vector3
  private archaeologyMaterial_: THREE.ShaderMaterial
  private archaeologyBlendMaterial_: THREE.ShaderMaterial
  private textureTransitionMaterial_: THREE.ShaderMaterial
  private archaeologyBlendQuad_: FullScreenQuad
  private archaeologyLayersEnabled_ = false
  private currentModelUrl_ = ''
  private modelLoadPromise_: Promise<GLTF> | undefined
  private cameraController_: CameraController
  private markerController_: MarkerController
  private canvas_: HTMLCanvasElement
  private threeMaterials_: ThreeMaterials = new ThreeMaterials()
  private threeLocation: ThreeLocation = new ThreeLocation()

  // Implementation of callbacks from `IThreeBurApp` interface,
  // because CameraController needs to call into the app on certain events.
  public updateMinimap({ position, dir }: MiniMapProps): void {
    this.updateMinimapCallback(position, dir)
  }

  public updatePOIMode(enabled: boolean): void {
    this.updatePoiModeCallback(enabled)
  }

  public leavePoiMode() {
    this.cameraController_.OnClickedExitPOIButton()
    this.markerController_.removeHighlightFromCurrentMarker()
  }

  public resetMarkers() {
    this.markerController_.resetMarkers()
  }

  public SetGeoPosition(geoPosition: GeolocationPosition) {
    const camera = this.cameraController_.GetCamera()
    const origin = this.cameraController_.GetOrigin()
    const worldPos = this.threeLocation.SetGeoPosition(geoPosition, this.scene_, camera, origin)

    if (worldPos) {
      this.render_ = true
      // The camera controller should also update to the new position inferred from the GPS location
      this.cameraController_.OnClickedGotoGeoPosition(worldPos)
    }
  }

  public showGeoPosition(show: boolean) {
    this.threeLocation.ShowGeoPosition(show, this.scene_, render => (this.render_ = render))
  }

  // NOTE: "SpriteLayer" refers to POI's and other camera facing markers.
  // Returns true if an intersection was found, and false otherwise.
  private tryIntersectWithSpriteLayer(): boolean {
    this.raycaster_.setFromCamera(this.pointerPosition_, this.cameraController_.GetCamera())
    this.raycaster_.layers.set(SPRITE_LAYER)

    const intersections = this.raycaster_.intersectObject(this.scene_)
    if (intersections.length <= 0) {
      return false
    }

    let closestHit = intersections[0]
    for (let i = 1; i < intersections.length; i++) {
      if (intersections[i].distance < closestHit.distance) {
        closestHit = intersections[i]
      }
    }

    // External function, which the calls back into this class with `flyToMarker`.
    this.POIWasClickedCallback_(parseInt(closestHit.object.name))

    return true
  }

  private tryIntersectWithAnyLayer(): boolean {
    // Picking scene
    this.raycaster_.setFromCamera(this.pointerPosition_, this.cameraController_.GetCamera())
    this.raycaster_.layers.set(0)

    const intersections = this.raycaster_.intersectObject(this.scene_)
    if (intersections.length <= 0) {
      return false
    }

    const intersection = intersections[0]
    this.cameraController_.OnClickedPosition(intersection)

    return true
  }

  private onMouseDownEvent(ev: MouseEvent): void {
    this.clickedPosition_[0] = ev.offsetX
    this.clickedPosition_[1] = ev.offsetY
  }

  private onMouseMoveEvent(ev: MouseEvent): void {
    this.pointerPosition_.set(
      ((ev.offsetX * window.devicePixelRatio) / this.canvas_.width) * 2 - 1,
      ((-ev.offsetY * window.devicePixelRatio) / this.canvas_.height) * 2 + 1
    )
  }

  private onMouseUpEvent(ev: MouseEvent): void {
    // Check to see if mouse position has changed since down event.
    // If it has, don't trigger intersection test and camera state change.
    const clickWithoutMove = ev.offsetX == this.clickedPosition_[0] && ev.offsetY == this.clickedPosition_[1]
    if (!clickWithoutMove) {
      return
    }

    this.pointerPosition_.set(
      ((ev.offsetX * window.devicePixelRatio) / this.canvas_.width) * 2 - 1,
      ((-ev.offsetY * window.devicePixelRatio) / this.canvas_.height) * 2 + 1
    )

    // Picking on sprites
    if (this.tryIntersectWithSpriteLayer()) {
      return
    }

    // If no sprite intersection, try a general intersection test
    if (this.tryIntersectWithAnyLayer()) {
      return
    }
  }

  constructor(canvas: HTMLCanvasElement, canvasContainer?: HTMLDivElement) {
    this.canvas_ = canvas
    this.scene_ = new THREE.Scene()
    this.raycaster_ = new THREE.Raycaster()
    this.pointerPosition_ = new THREE.Vector2()
    this.POIWasClickedCallback_ = () => undefined
    this.updateMinimapCallback = () => undefined
    this.updatePoiModeCallback = () => undefined
    this.canvasContainer = canvasContainer
    this.opacityTransition_ = { opacity: 0 }
    this.modelBoundingBox_ = new THREE.Box3()
    this.modelCenter_ = new THREE.Vector3()
    this.modelSize_ = new THREE.Vector3()

    THREE.Cache.enabled = true

    // Prevent double click select outside canvas
    canvas.onselectstart = function () {
      return false
    }

    this.renderer_ = new THREE.WebGLRenderer({
      canvas: canvas,
      antialias: true,
    })
    this.renderer_.shadowMap.enabled = this.shadowsEnabled_
    this.renderer_.shadowMap.autoUpdate = false
    this.renderer_.shadowMap.type = THREE.PCFShadowMap

    // Tonemapping
    this.renderer_.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer_.toneMappingExposure = 0.88

    // Fbos for transition
    this.fboBg_ = new THREE.WebGLRenderTarget(canvas.width, canvas.height, { samples: 4 })
    this.fboFg_ = new THREE.WebGLRenderTarget(canvas.width, canvas.height, { samples: 4 })
    // Fbo for heightmap.
    // The heightmap is ideally a single channel 8 bit image. Unfortunately
    // som WebGL implementations do not allow pixelreadback from gl.RED
    // format, so we store all four channels.
    this.heightmapFbo_ = new THREE.WebGLRenderTarget(1024, 1024, { format: THREE.RGBAFormat })
    this.heightmapData_ = new Uint8Array(4 * this.heightmapFbo_.height * this.heightmapFbo_.width)
    this.depthMaterial_ = new THREE.MeshDepthMaterial()

    const ktx2Loader = new KTX2Loader().detectSupport(this.renderer_)
    this.loader_ = new GLTFLoader()
    this.loader_.setKTX2Loader(ktx2Loader)
    this.loader_.setMeshoptDecoder(MeshoptDecoder)

    const environment = new RoomEnvironment(this.renderer_)

    this.pmremGenerator_ = new THREE.PMREMGenerator(this.renderer_)

    this.scene_.environment = this.pmremGenerator_.fromScene(environment).texture

    // the camera's x and y of position must be inside aabb.(min|max)!
    const aabb = this.getSceneBoundingBox(this.scene_)

    const light = new THREE.AmbientLight(0xffffbb, 2)
    const dirlight = new THREE.DirectionalLight(0xffffff, 4)
    dirlight.position.set(-239.29126093907627, 482.5207204827611, 290.8841747600806)
    dirlight.castShadow = this.shadowsEnabled_
    if (this.shadowsEnabled_) {
      // Setup shadowmap
      dirlight.shadow.bias = 0.00001
      dirlight.shadow.mapSize.width = 2048

      dirlight.shadow.mapSize.height = 2048
      dirlight.shadow.camera.near = 500
      dirlight.shadow.camera.far = 1000
      dirlight.shadow.camera.left = -300
      dirlight.shadow.camera.right = 400
      dirlight.shadow.camera.top = 300
      dirlight.shadow.camera.bottom = -200
      dirlight.shadow.camera.updateProjectionMatrix()
    }

    this.light_ = [light, dirlight]

    // Setting up the camera
    this.clickedPosition_ = [-1, -1]
    const cameraPos = new THREE.Vector3(83.04123049939878, 117.28613424722646, -105.2112037891568)
    const cameraLookAt = new THREE.Vector3(45, 0, -182)
    this.cameraController_ = new CameraController(canvas, cameraPos, cameraLookAt, this)
    this.markerController_ = new MarkerController(this.scene_, this.raycaster_, this.cameraController_)

    // Setup pointer events on the canvas. Note: do not use pointerdown/up
    // because it closes the modal immediately
    canvas.addEventListener('mousedown', ev => this.onMouseDownEvent(ev), false)
    canvas.addEventListener('mousemove', ev => this.onMouseMoveEvent(ev), false)
    canvas.addEventListener('mouseup', ev => this.onMouseUpEvent(ev), false)

    // Create shaders
    this.archaeologyMaterial_ = this.threeMaterials_.createArcheologyMaterial()
    this.archaeologyBlendMaterial_ = this.threeMaterials_.createArcheologyBlendMaterial(this.fboFg_.texture)
    this.textureTransitionMaterial_ = this.threeMaterials_.createTextureTransitionMaterial(
      this.fboFg_.texture,
      this.fboBg_.texture
    )

    const fsQuad = new FullScreenQuad(this.textureTransitionMaterial_)
    this.archaeologyBlendQuad_ = new FullScreenQuad(this.archaeologyBlendMaterial_)

    // Renderloop
    const loop = () => {
      if (canvasContainer !== undefined) {
        if (canvasContainer.clientWidth !== canvas.clientWidth || canvasContainer.clientHeight !== canvas.clientHeight) {
          this.resize(canvasContainer.clientWidth, canvasContainer.clientHeight)
        }
      }

      this.cameraController_.updateCameraIdleTimer()

      if (TWEEN.update() || this.render_ || this.cameraController_.ShouldRender()) {
        const props = this.cameraController_.calcCameraPositionAndDirection()
        this.updateMinimap(props)
        if (this.opacityTransition_.opacity != 1.0) {
          this.textureTransitionMaterial_.uniforms['mixfactor'].value = this.opacityTransition_.opacity
          fsQuad.render(this.renderer_)
        } else {
          if (this.archaeologyLayersEnabled_) {
            this.renderArchaeology(this.fboFg_, null)
          } else {
            this.renderer_.render(this.scene_, this.cameraController_.GetCamera())
          }
        }
        this.render_ = false
      }
      requestAnimationFrame(loop)
    }

    this.render_ = true
    requestAnimationFrame(loop)
  }

  /**
   * Call this function every time a (new) scene / gltf has been loaded.
   * It calculates scene boundaries, which e.g. the camera controller needs.
   */
  public OnPostLoadScene(): void {
    this.cameraController_.SetSceneExtent(this.getSceneBoundingBox(this.scene_))
  }

  /*
   * Render all non-archaeology meshes to sceneFbo
   * Render archaeology mesh to target fbo
   * Blend combine in the target fbo
   */
  private renderArchaeology(sceneFbo: THREE.WebGLRenderTarget, target: THREE.WebGLRenderTarget | null) {
    const camera = this.cameraController_.GetCamera()

    // Render overground to sceneFbo
    camera.layers.enableAll()
    camera.layers.disable(ARCHAEOLOGY_LAYER)
    this.renderSceneOffscreen(sceneFbo)

    // Render archaeology layers to target
    const oldbackground = this.scene_.background
    this.scene_.background = null
    this.renderer_.setRenderTarget(target)

    camera.layers.set(ARCHAEOLOGY_LAYER)
    this.renderer_.render(this.scene_, camera)

    this.renderer_.autoClear = false
    this.archaeologyBlendQuad_.render(this.renderer_)
    this.renderer_.autoClear = true
    this.scene_.background = oldbackground

    camera.layers.enableAll()
    camera.layers.disable(ARCHAEOLOGY_LAYER)
  }

  private getSceneBoundingBox(scene: THREE.Object3D): THREE.Box3 {
    const aabb = new THREE.Box3()
    aabb.makeEmpty()
    aabb.setFromObject(scene)
    return aabb
  }

  /*
   * Create a heightmap by rendering the scene from above using a depth-only material
   */
  private createHeightmap() {
    // Create a scene with depth-only material
    const simplescene = new THREE.Scene()

    // Do not use traverse when modifying the tree
    this.scene_.children.forEach(obj => {
      if (obj instanceof THREE.Group) {
        simplescene.add(obj.clone())
      }
    })
    simplescene.traverse(obj => {
      if (obj instanceof THREE.Mesh) {
        obj.material = this.depthMaterial_
      }
    })

    // Setup orthographic camera to cover the scene
    const bb = this.modelBoundingBox_
    const center = new THREE.Vector3()
    const size = new THREE.Vector3()
    bb.getCenter(center)
    bb.getSize(size)
    const orthoCamera = new THREE.OrthographicCamera(-0.5 * size.x, 0.5 * size.x, 0.5 * size.z, -0.5 * size.z, 1, 1 + size.y)

    orthoCamera.position.set(center.x, bb.max.y + 1, center.z)
    orthoCamera.lookAt(center.x, bb.min.y, center.z)

    // Render the depth from orthoCamera
    const oldviewport = new THREE.Vector4()
    this.renderer_.getViewport(oldviewport)
    this.renderer_.setRenderTarget(this.heightmapFbo_)
    this.renderer_.setViewport(0, 0, this.heightmapFbo_.width, this.heightmapFbo_.height)
    this.renderer_.render(simplescene, orthoCamera)

    // Readback pixels
    this.renderer_.readRenderTargetPixels(
      this.heightmapFbo_,
      0,
      0,
      this.heightmapFbo_.width,
      this.heightmapFbo_.height,
      this.heightmapData_
    )
    // Restore old state
    this.renderer_.setRenderTarget(null)
    this.renderer_.setViewport(oldviewport)
    simplescene.clear()
  }

  // Get landscape height at `position` coordinate, y ignored since y is our world up-axis.
  public getHeightAtPosition(position: THREE.Vector3): number {
    // Compute the 3d texture coordinates [uvx, uvy, uvz] within the bounding box
    const uvx = (position.x - this.modelBoundingBox_.min.x) / this.modelSize_.x
    const uvy = (this.modelBoundingBox_.max.z - position.z) / this.modelSize_.z

    // Lookup the height in the red channel of the heightmap
    const ix = Math.max(0, Math.min(Math.round(uvx * this.heightmapFbo_.width), this.heightmapFbo_.width - 1))
    const iy = Math.max(0, Math.min(Math.round(uvy * this.heightmapFbo_.height), this.heightmapFbo_.height - 1))

    const uvz = this.heightmapData_[4 * (ix + iy * this.heightmapFbo_.width)] / 255.0

    // Scale the height uv coordinate to real position space
    return uvz * this.modelSize_.y + this.modelBoundingBox_.min.y
  }

  /*
   * Set archaeology layers visibility
   */
  setArchaeologyLayersEnabled(enabled: boolean) {
    if (this.archaeologyLayersEnabled_ == enabled) {
      return
    }

    // Setup textures for smooth transition
    this.renderArchaeology(this.fboFg_, this.fboBg_)

    this.renderer_.setRenderTarget(null)

    this.archaeologyLayersEnabled_ = enabled

    // Smooth transition, either from fg to bg or reverse
    this.opacityTransition_.opacity = enabled ? 1 : 0
    new TWEEN.Tween(this.opacityTransition_)
      .to({ opacity: enabled ? 0 : 1 }, 200)
      .easing(TWEEN.Easing.Quartic.In)
      .start()
      .onComplete(() => (this.opacityTransition_.opacity = 1))
  }

  /*
   * Get archaeology layers visibility state
   */
  getArchaeologyLayersEnabled(): boolean {
    return this.archaeologyLayersEnabled_
  }

  private setSkyboxCubemap(filenames: string[]): void {
    const loader = new THREE.CubeTextureLoader()
    const cubeTexture = loader.load(filenames)
    cubeTexture.colorSpace = THREE.SRGBColorSpace
    this.scene_.background = cubeTexture
  }

  // App / rendering configuration depending on device
  public setDeviceType(deviceType: string, loadPath: string = '') {
    //
    // mobile
    //
    if (deviceType == 'mobile') {
      this.setSkyboxCubemap([
        loadPath + 'skybox_512/px.jpg',
        loadPath + 'skybox_512/nx.jpg',
        loadPath + 'skybox_512/py.jpg',
        loadPath + 'skybox_512/ny.jpg',
        loadPath + 'skybox_512/pz.jpg',
        loadPath + 'skybox_512/nz.jpg',
      ])
    }
    //
    // desktop
    //
    else if (deviceType == 'desktop') {
      this.setSkyboxCubemap([
        loadPath + 'skybox_1024/px.jpg',
        loadPath + 'skybox_1024/nx.jpg',
        loadPath + 'skybox_1024/py.jpg',
        loadPath + 'skybox_1024/ny.jpg',
        loadPath + 'skybox_1024/pz.jpg',
        loadPath + 'skybox_1024/nz.jpg',
      ])
    }
    //
    // museum
    //
    else {
      // Suggested skybox for museum presentation (big screen equals big texture)
      this.setSkyboxCubemap([
        loadPath + 'skybox_2048/px.jpg',
        loadPath + 'skybox_2048/nx.jpg',
        loadPath + 'skybox_2048/py.jpg',
        loadPath + 'skybox_2048/ny.jpg',
        loadPath + 'skybox_2048/pz.jpg',
        loadPath + 'skybox_2048/nz.jpg',
      ])
    }
  }

  /**
   * Called externally to specify a callback.
   */
  setPOIWasClickedCallback(f: (s: number) => void): void {
    this.POIWasClickedCallback_ = f
  }

  setUpdateMinimapCallback(f: (position: number[], direction: number[]) => void): void {
    this.updateMinimapCallback = f
  }

  setPoiModeCallback(f: (enabled: boolean) => void): void {
    this.updatePoiModeCallback = f
  }

  public flyToMarker(marker: { position: Location; id: number }): void {
    const geopos = fromLatLon(marker.position.latitude, marker.position.longitude, UTMZONE)
    const origin = this.cameraController_.GetOrigin()

    // x, z is world position, and we may define a suitable height value as y, for a complete
    // 3D point of where the marker is.
    const x = geopos.easting - origin.x
    const z = origin.y - geopos.northing
    let height = 50

    if (this.markerController_.hasMarker(marker.id)) {
      height = this.markerController_.getMarkerYPosition(marker.id)
      this.markerController_.setMarkerAsHighlighted(marker.id, SPRITE_LAYER)
    } else {
      this.raycaster_.layers.set(0)
      this.raycaster_.set(new THREE.Vector3(x, 1000, z), new THREE.Vector3(0, -1, 0))
      const intersections = this.raycaster_.intersectObject(this.scene_)

      if (intersections.length > 0) {
        height = intersections[0].point.y + 20
      }
    }

    const pos = new THREE.Vector3(x, height, z)
    this.cameraController_.OnClickedPOI(pos)
  }

  public flyToPositionWithDelay(position: Location, delay: number): void {
    setTimeout(() => {
      this.flyToPosition(position)
    }, delay)
  }

  public flyToAssetWithDelay(asset: Asset, delay: number): void {
    setTimeout(() => {
      this.flyToAsset(asset)
    }, delay)
  }

  public flyToAsset(asset: Asset, disableTween?: boolean): void {
    const geopos = fromLatLon(asset.location.latitude, asset.location.longitude, UTMZONE)
    const origin = this.cameraController_.GetOrigin()
    const x = geopos.easting - origin.x
    const y = origin.y - geopos.northing

    let height = 50
    this.raycaster_.layers.set(0)
    this.raycaster_.set(new THREE.Vector3(x, 1000, y), new THREE.Vector3(0, -1, 0))
    const intersections = this.raycaster_.intersectObject(this.scene_)

    if (intersections.length > 0) {
      height = intersections[0].point.y + 5
    }

    const position = new THREE.Vector3(x, height, y)
    this.cameraController_.OnFlyToAssetFromUrl(position)
  }

  private flyToPosition(position: Location, disableTween?: boolean): void {
    const geopos = fromLatLon(position.latitude, position.longitude, UTMZONE)
    const origin = this.cameraController_.GetOrigin()
    const x = geopos.easting - origin.x
    const y = origin.y - geopos.northing

    let height = 50
    this.raycaster_.layers.set(0)
    this.raycaster_.set(new THREE.Vector3(x, 1000, y), new THREE.Vector3(0, -1, 0))
    const intersections = this.raycaster_.intersectObject(this.scene_)

    if (intersections.length > 0) {
      height = intersections[0].point.y + 20
    }

    const useTween = !disableTween?.valueOf() ?? true // Invert the boolean and remove possibly undefined
    const tweenDurationMs = 2000
    this.cameraController_.FlyToPosition(height, x, y, useTween, tweenDurationMs)
  }

  /**
   * Reset view to fit scene
   */
  resetView(): void {
    this.cameraController_.ResetCameraView(this.modelBoundingBox_)
  }

  async loadModelFromUrl(filename: string, markers?: { position: Location; id: number }[]): Promise<void> {
    if (filename === '') {
      this.render_ = true
      return
    }

    // Prevent running this async method multiple times concurrently
    if (this.modelLoadPromise_) {
      await this.modelLoadPromise_
    }

    if (filename == this.currentModelUrl_) {
      // Only recreate markers if the model filename is unchanged
      this.currentModelUrl_ = filename
      this.markerController_.createMarkers(SPRITE_LAYER, UTMZONE, markers)
    } else {
      this.currentModelUrl_ = filename
      this.modelLoadPromise_ = this.loader_.loadAsync(filename)
      const gltf = await this.modelLoadPromise_
      this.parseGLTF(gltf, markers)
    }
  }

  async loadModel(gltfdata: Blob, markers?: { position: Location; id: number }[]): Promise<void> {
    const gltfbuffer = await gltfdata.arrayBuffer()
    const gltf = await this.loader_.parseAsync(new Uint8Array(gltfbuffer), '')

    this.parseGLTF(gltf, markers)
  }

  /*
   * Clear scene and free webgl resources
   */
  private clearScene() {
    // Remove existing
    this.scene_.traverse(node => {
      if (node instanceof THREE.Mesh) {
        // Dispose gpu memory
        node.geometry.dispose()
        if (node.material.map && node.material.map.isTexture) {
          node.material.map.dispose()
        }
        if (node.material.metalnessMap && node.material.metalnessMap.isTexture) {
          node.material.metalnessMap.dispose()
        }
        if (node.material.roughnessMap && node.material.roughnessMap.isTexture) {
          node.material.roughnessMap.dispose()
        }
        if (node.material.normalMap && node.material.normalMap.isTexture) {
          node.material.normalMap.dispose()
        }
        node.material.dispose()
      }
      if (node instanceof THREE.Sprite) {
        node.geometry.dispose()
        if (node.material.map && node.material.map.isTexture) {
          node.material.map.dispose()
        }
        node.material.dispose()
      }
    })
    this.scene_.clear()

    this.archaeologyLayersEnabled_ = false

    this.markerController_.markers_ = {}
  }

  private renderSceneOffscreen(fbo: THREE.WebGLRenderTarget) {
    this.renderer_.setRenderTarget(fbo)
    this.renderer_.render(this.scene_, this.cameraController_.GetCamera())
    this.renderer_.setRenderTarget(null)
  }

  private parseGLTF(gltf: GLTF, markers?: { position: Location; id: number }[]) {
    // Render the current scene to background fbo
    this.renderSceneOffscreen(this.fboBg_)

    this.clearScene()

    // Assume geotransform is encoded in the first child node
    if (gltf.scene.children && gltf.scene.children.length > 0) {
      gltf.scene.traverse(node => {
        if (node instanceof THREE.Mesh || node instanceof THREE.Line) {
          const oldMaterial = node.material
          // GLTF pbr materials seem to be too glossy. Replace by simple
          // diffuse
          if (oldMaterial instanceof THREE.MeshStandardMaterial) {
            node.material = new THREE.MeshLambertMaterial({
              name: oldMaterial.name,
              map: oldMaterial.map,
              color: oldMaterial.color,
            })

            if (node.name.startsWith('archeology') || node.name.startsWith('archaeology')) {
              node.material = this.archaeologyMaterial_
            }
          }
          if (node.name.startsWith('archeology') || node.name.startsWith('archaeology')) {
            node.layers.set(ARCHAEOLOGY_LAYER)
          }

          if (this.shadowsEnabled_) {
            node.castShadow = true
            // Hacky method to let shadows only affect terrain. This should
            // be pre-baked in the final model
            if (oldMaterial.name.search(/gravel|grass|R172/i) > -1) {
              node.receiveShadow = true
            }
          }
        }
      })

      this.cameraController_.SetOrigin(gltf.scene.children[0].position.x, gltf.scene.children[0].position.z)
      gltf.scene.children[0].position.setX(0.0)
      gltf.scene.children[0].position.setZ(0.0)

      this.scene_.add(gltf.scene)
      // Store the actual bounding box of the geometry before adding light
      // and sprites to the scene
      this.modelBoundingBox_.copy(this.getSceneBoundingBox(gltf.scene))
      this.modelBoundingBox_.getSize(this.modelSize_)
      this.modelBoundingBox_.getCenter(this.modelCenter_)

      this.light_.forEach(light => {
        this.scene_.add(light)
      })
    }

    this.createHeightmap()

    this.markerController_.createMarkers(SPRITE_LAYER, UTMZONE, markers)

    if (this.shadowsEnabled_) {
      this.renderer_.shadowMap.needsUpdate = true
    }

    // The scene is ready. Render to the foreground fbo
    this.renderSceneOffscreen(this.fboFg_)

    // Smooth transition
    this.opacityTransition_.opacity = 0.0
    new TWEEN.Tween(this.opacityTransition_).to({ opacity: 1.0 }, 1000).easing(TWEEN.Easing.Quartic.In).start()
  }

  resize = (w: number, h: number) => {
    this.cameraController_.UpdateViewportSize(w, h)
    this.renderer_.setPixelRatio(window.devicePixelRatio)
    this.renderer_.setSize(w, h)
    this.render_ = true
    this.fboBg_.setSize(w, h)
    this.fboFg_.setSize(w, h)
  }
}

export { ThreeBur3DView }
